import { useRef, useEffect } from "react";
import { Link } from "react-router-dom";
import { Ghost, Snowflake } from "lucide-react";
import { motion, useScroll, useTransform } from "framer-motion";
import SEO from "../components/SEO";
import EditorialMarquee from "../components/Marquee";
import { FadeIn, Overline, ArrowButton } from "../components/Reveal";
import { IMG, SERVICES, CONTACT } from "../data/site";

const lines = ["Traiteur événementiel", "à Lyon", "et Valence."];

const jsonLd = [
  {
    "@context": "https://schema.org",
    "@type": "Organization",
    name: "Maison Désir & Gourmandise",
    "@id": "https://gourmandise-traiteur.fr/#org",
    url: "https://gourmandise-traiteur.fr/",
    logo: "https://gourmandise-traiteur.fr/photos/x33itvhe.webp",
    telephone: "+33484507142",
    email: "devis@gourmandise-traiteur.fr",
    address: [
      { "@type": "PostalAddress", streetAddress: "Parc du Chêne", addressLocality: "Bron", postalCode: "69500", addressCountry: "FR" },
      { "@type": "PostalAddress", streetAddress: "105 rue des Mourettes", addressLocality: "Valence", postalCode: "26000", addressCountry: "FR" },
    ],
  },
  {
    "@context": "https://schema.org",
    "@type": "FoodEstablishment",
    name: "Maison Désir & Gourmandise",
    "@id": "https://gourmandise-traiteur.fr/",
    url: "https://gourmandise-traiteur.fr/",
    image: "/photos/x33itvhe.webp",
    description:
      "Traiteur événementiel à Lyon, Valence et Grenoble : cocktails, buffets sur-mesure, plateaux-repas entreprise, mariages et animation brasero & paella.",
    telephone: "+33484507142",
    email: "devis@gourmandise-traiteur.fr",
    foundingDate: "2012",
    priceRange: "€€",
    areaServed: ["Lyon", "Valence", "Grenoble", "Drôme", "Ardèche", "Chambéry"],
    address: [
      { "@type": "PostalAddress", streetAddress: "Parc du Chêne", addressLocality: "Bron", postalCode: "69500", addressCountry: "FR" },
      { "@type": "PostalAddress", streetAddress: "105 rue des Mourettes", addressLocality: "Valence", postalCode: "26000", addressCountry: "FR" },
    ],
  },
];

export default function Home() {
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const textY = useTransform(scrollYProgress, [0, 1], ["0%", "35%"]);

  // Bandeaux saisonniers : Halloween du 1er oct. au 1er nov., Noël du 2 nov. au 31 déc.
  // (avant-premières possibles via ?halloween=1 et ?noel=1)
  const now = new Date();
  const params = new URLSearchParams(window.location.search);
  const showHalloween =
    now.getMonth() === 9 || (now.getMonth() === 10 && now.getDate() === 1) || params.has("halloween");
  const showNoel =
    (now.getMonth() === 10 && now.getDate() > 1) || now.getMonth() === 11 || params.has("noel");
  const showHiver = now.getMonth() >= 10 || now.getMonth() <= 2 || params.has("hiver");

  // Accessibilité : pas de vidéo en lecture auto si l'utilisateur réduit les animations
  useEffect(() => {
    const v = document.querySelector('[data-testid="hero-video"]');
    if (v && window.matchMedia("(prefers-reduced-motion: reduce)").matches) v.pause();
  }, []);

  return (
    <div data-testid="page-accueil">
      <SEO
        title="Traiteur Lyon & Valence | Mariages, entreprises et réceptions"
        description="Maison Désir & Gourmandise, traiteur événementiel depuis 2012 à Lyon et Valence. Cocktails créatifs, buffets sur-mesure, plateaux-repas entreprise, mariages et animation brasero & paella. Devis gratuit."
        path="/"
        jsonLd={jsonLd}
      />

      {/* BANDEAU SAISONNIER HALLOWEEN (1er oct. → 1er nov.) */}
      {showHalloween && (
        <Link
          to="/devis"
          data-testid="bandeau-halloween"
          className="group relative z-30 mt-20 flex items-center justify-center gap-3 bg-[#1C1210] px-6 py-3.5 text-center text-sm font-semibold text-white transition-colors duration-300 hover:bg-[#2b1a13]"
        >
          <Ghost className="h-4 w-4 shrink-0 text-[#F97316]" />
          <span>Joyeux Halloween — pensez à réserver vos réceptions d'automne et de fin d'année</span>
          <span className="hidden text-[#F97316] underline underline-offset-4 sm:inline">Demander un devis →</span>
        </Link>
      )}

      {/* BANDEAU SAISONNIER NOËL (2 nov. → 31 déc.) */}
      {showNoel && (
        <Link
          to="/menus-noel"
          data-testid="bandeau-noel"
          className="group relative z-30 mt-20 flex items-center justify-center gap-3 bg-[#A6192E] px-6 py-3.5 text-center text-sm font-semibold text-white transition-colors duration-300 hover:bg-[#1C1210]"
        >
          <span>Nos menus de Noël et goûters d'entreprise sont arrivés — dès 9 €/pers. TTC</span>
          <span className="hidden underline underline-offset-4 sm:inline">Découvrir →</span>
        </Link>
      )}

      {/* HERO */}
      <section ref={heroRef} className="relative min-h-screen overflow-hidden bg-[#2A1D1A] pt-20">
        <motion.div style={{ y: imgY }} className="absolute inset-0">
          {/* Vidéo promo saisonnière en fond d'ouverture (poster = image hero, fallback si reduced motion) */}
          <video
            data-testid="hero-video"
            className="h-[115%] w-full object-cover opacity-45"
            autoPlay
            muted
            loop
            playsInline
            preload="metadata"
            poster={IMG.hero}
            aria-hidden="true"
          >
            <source src="/videos/promo-noel.mp4" type="video/mp4" />
          </video>
          <img
            src={IMG.hero}
            alt="Assiette gastronomique dressée par Maison Désir & Gourmandise, traiteur à Lyon"
            className="sr-only"
            loading="eager"
            fetchPriority="high"
            decoding="async"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#2A1D1A] via-[#2A1D1A]/40 to-[#2A1D1A]/30" />
        </motion.div>

        <motion.div style={{ y: textY }} className="relative z-10 mx-auto flex min-h-[calc(100vh-5rem)] max-w-7xl flex-col justify-center px-6 pb-24 lg:px-10">
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.9, duration: 1 }}
            className="mb-8 text-xs uppercase tracking-[0.35em] text-[#CBA135]"
          >
            Traiteur événementiel — Entreprises, mariages & réceptions
          </motion.p>

          <h1 className="font-serif-display text-[13vw] font-light leading-[0.95] tracking-tight text-[#FAF8F5] sm:text-7xl lg:text-8xl">
            {lines.map((line, i) => (
              <span key={line} className="block overflow-hidden pb-1">
                <motion.span
                  initial={{ y: "110%" }}
                  animate={{ y: "0%" }}
                  transition={{ duration: 1.1, delay: 0.15 + i * 0.14, ease: [0.22, 1, 0.36, 1] }}
                  className="block"
                >
                  {i === 2 ? <span className="italic text-[#CBA135]">{line}</span> : line}
                </motion.span>
              </span>
            ))}
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.1, duration: 0.9 }}
            className="mt-10 max-w-xl text-base leading-relaxed text-[#D8CCC2] md:text-lg"
          >
            <span className="font-serif-display text-xl italic text-[#FAF8F5] md:text-2xl">
              Le traiteur qui fait de vos événements un souvenir inoubliable.
            </span>
            <span className="mt-3 block">
              Produits frais faits maison, menus 100% sur-mesure : dites-nous la date et le nombre
              de convives, <span className="text-[#CBA135]">nous nous occupons de tout</span>.
            </span>
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.3, duration: 0.9 }}
            className="mt-12 flex flex-wrap items-center gap-5"
          >
            <ArrowButton to="/devis" dark testId="hero-cta-devis">Calculer mon budget</ArrowButton>
            <a
              href="#offres"
              data-testid="hero-cta-formules"
              className="rounded-full border border-[#4a3833] px-8 py-4 text-sm font-semibold text-[#FAF8F5] transition-colors duration-300 hover:border-[#CBA135] hover:text-[#CBA135]"
            >
              Voir nos formules
            </a>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.5, duration: 0.9 }}
            className="mt-14 grid max-w-3xl grid-cols-1 gap-4 sm:grid-cols-3"
            data-testid="hero-arguments"
          >
            {[
              ["Produits frais, faits maison", "Cuisine d'atelier, produits du terroir, aucun plat industriel."],
              ["Devis en 24h", "Réponse détaillée et chiffrée sous 24 heures ouvrées, garanti."],
              ["100% sur-mesure", "Chaque menu est composé selon vos goûts, votre budget, vos invités."],
            ].map(([t, d]) => (
              <div key={t} className="border-l-2 border-[#CBA135]/70 pl-4">
                <p className="text-sm font-bold text-[#FAF8F5]">{t}</p>
                <p className="mt-1 text-xs leading-relaxed text-[#B8A99F]">{d}</p>
              </div>
            ))}
          </motion.div>
        </motion.div>
      </section>

      <EditorialMarquee />

      {/* SECTION HIVER (1er nov. → 31 mars, avant-première via ?hiver=1) */}
      {showHiver && (
        <section className="bg-[#1C1210] py-24" data-testid="section-hiver">
          <div className="mx-auto max-w-7xl px-6 lg:px-10">
            <FadeIn>
              <p className="flex items-center gap-3 text-xs uppercase tracking-[0.35em] text-[#CBA135]">
                <Snowflake className="h-4 w-4" /> L'hiver à table — de novembre à mars
              </p>
              <h2 className="mt-4 max-w-2xl font-serif-display text-4xl font-light leading-tight text-[#FAF8F5] md:text-5xl">
                Les repas d'hiver qui <span className="italic text-[#CBA135]">rassemblent</span> vos équipes.
              </h2>
              <p className="mt-5 max-w-2xl text-base leading-relaxed text-[#B8A99F]">
                Raclette, fondue, tartiflette et plateaux montagnards : des formules d'hiver à prix clairs TTC,
                livrées sur Lyon, Valence et Grenoble. Alternative végétarienne sur chaque formule.
              </p>
            </FadeIn>
            <div className="mt-14 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
              {[
                { name: "Raclette conviviale", price: "Dès 22,90 € / pers.", min: "Dès 20 convives", desc: "Fromage à raclette, charcuteries, pommes de terre vapeur et condiments.", to: "/traiteur-raclette" },
                { name: "Fondue savoyarde", price: "Dès 19,90 € / pers.", min: "Dès 20 convives", desc: "Mélange de fromages, pain et mâche, prêt à préparer sur place.", to: "/traiteur-fondue-savoyarde" },
                { name: "Plats chauds d'hiver", price: "Dès 14,90 € / pers.", min: "Dès 25 convives", desc: "Tartiflette en grand contenant ou gratin de ravioles aux champignons.", to: "/plats-chauds-hiver-traiteur" },
                { name: "Plateau apéritif montagnard", price: "Dès 12,90 € / pers.", min: "Dès 10 convives", desc: "Fromages, charcuteries, pain et condiments pour l'afterwork.", to: "/plateaux-aperitifs-hiver" },
              ].map((o, i) => (
                <FadeIn key={o.name} delay={i * 0.06}>
                  <div data-testid={`hiver-card-${i}`} className="group flex h-full flex-col rounded-3xl border border-[#3d2b26] p-7 transition-colors duration-300 hover:border-[#CBA135]">
                    <h3 className="font-serif-display text-xl font-medium text-[#FAF8F5]">{o.name}</h3>
                    <span className="mt-3 inline-block self-start rounded-full bg-[#CBA135]/15 px-4 py-1.5 font-serif-display text-base font-medium text-[#CBA135]">{o.price}</span>
                    <p className="mt-3 text-sm leading-relaxed text-[#B8A99F]">{o.desc}</p>
                    <p className="mt-2 text-xs text-[#7A6B63]">{o.min} — matériel et livraison sur devis</p>
                    <div className="mt-6 flex flex-1 items-end">
                      <Link to={o.to} data-testid={`hiver-detail-${i}`} className="text-sm font-semibold text-[#CBA135] transition-colors hover:text-[#FAF8F5]">
                        Détails →
                      </Link>
                    </div>
                  </div>
                </FadeIn>
              ))}
            </div>
            <FadeIn delay={0.15} className="mt-10">
              <Link to="/repas-hiver-entreprise" data-testid="hiver-link-hub" className="text-sm font-semibold text-[#FAF8F5] underline underline-offset-4 transition-colors hover:text-[#CBA135]">
                Toutes les formules repas d'hiver entreprise →
              </Link>
            </FadeIn>
          </div>
        </section>
      )}

      {/* OFFRES VEDETTES */}
      <section id="offres" className="mx-auto max-w-7xl scroll-mt-24 px-6 py-28 lg:px-10">
        <FadeIn>
          <Overline>Nos formules les plus demandées</Overline>
          <h2 className="mt-4 max-w-2xl font-serif-display text-4xl font-light leading-tight text-[#2A1D1A] md:text-5xl">
            Six offres, <span className="italic text-[#C05A45]">prix clairs</span>, devis en 2 minutes.
          </h2>
          <p className="mt-4 text-xs uppercase tracking-[0.15em] text-[#7A6B63]">Tarifs TTC — livraison incluse sur Lyon, Valence et Grenoble</p>
          <p className="mt-3 text-sm text-[#7A6B63]">
            Entreprise lyonnaise ?{" "}
            <Link to="/plateau-repas-entreprise-lyon" data-testid="home-link-plateaux-lyon" className="font-semibold text-[#C05A45] transition-colors hover:text-[#2A1D1A]">
              Livraison plateaux-repas à Lyon
            </Link>{" "}
            — bureaux, réunions et séminaires, commande la veille avant 16 h.
          </p>
        </FadeIn>
        <div className="mt-16 grid grid-cols-1 gap-8 sm:grid-cols-2 lg:grid-cols-3">
          {[
            { name: "Brasero au feu de bois", price: "Dès 30 € / pers.", min: "À partir de 30 convives", to: "/brasero", img: "/photos/x33itvhe.webp", alt: "Brochettes grillées sur notre brasero au feu de bois" },
            { name: "Paella géante en live", price: "Dès 15 € / pers.", min: "À partir de 30 convives", to: "/paella", img: "/photos/dpuk8u65.webp", alt: "Notre chef dressant la paella géante devant les invités" },
            { name: "Cocktail entreprise", price: "Dès 25 € / pers.", min: "À partir de 20 convives", to: "/pieces-cocktail", img: "/photos/qulwxp0r.webp", alt: "Pièce cocktail dressée : tartare de saumon, concombre et agrumes" },
            { name: "Buffet réception", price: "Dès 26 € / pers.", min: "À partir de 30 convives", to: "/buffets", img: "/photos/u-15337778.webp", alt: "Buffet de réception généreux et élégant" },
            { name: "Mariage clé en main", price: "Dès 55 € / pers.", min: "À partir de 50 convives", to: "/traiteur-mariage-lyon", img: "/photos/u-15331209.webp", alt: "Table de mariage dressée en extérieur" },
            { name: "Plateau-repas entreprise", price: "Dès 14,90 € (plat seul)", min: "À partir de 10 plateaux", to: "/plateaux-repas", img: "/photos/livraison-plateaux.webp", alt: "Livraison de plateaux-repas en entreprise par Maison Désir & Gourmandise" },
          ].map((o, i) => (
            <FadeIn key={o.name} delay={i * 0.06}>
              <div data-testid={`offer-card-${i}`} className="group flex h-full flex-col overflow-hidden rounded-3xl border border-[#E8E2D9] bg-[#FAF8F5]">
                <div className="overflow-hidden">
                  <img src={o.img} alt={o.alt} loading="lazy" className="aspect-[16/10] w-full object-cover transition-transform duration-700 group-hover:scale-105" />
                </div>
                <div className="flex flex-1 flex-col p-7">
                  <h3 className="font-serif-display text-2xl font-medium text-[#2A1D1A]">{o.name}</h3>
                  <div className="mt-3 flex items-center gap-3">
                    <span className="rounded-full bg-[#C05A45]/10 px-4 py-1.5 font-serif-display text-lg font-medium text-[#C05A45]">{o.price}</span>
                    <span className="text-xs text-[#7A6B63]">{o.min}</span>
                  </div>
                  <div className="mt-6 flex flex-1 items-end gap-4">
                    <Link to="/devis" data-testid={`offer-devis-${i}`} className="rounded-full bg-[#C05A45] px-6 py-2.5 text-sm font-semibold text-[#FAF8F5] transition-colors duration-300 hover:bg-[#2A1D1A]">
                      Obtenir mon devis
                    </Link>
                    <Link to={o.to} data-testid={`offer-detail-${i}`} className="text-sm font-semibold text-[#7A6B63] transition-colors hover:text-[#C05A45]">
                      Détails →
                    </Link>
                  </div>
                </div>
              </div>
            </FadeIn>
          ))}
        </div>
        <FadeIn delay={0.15} className="mt-10">
          <p className="text-center text-sm text-[#7A6B63]">
            Chaque offre existe en trois niveaux : <strong className="text-[#2A1D1A]">livraison simple</strong>, <strong className="text-[#2A1D1A]">animation avec cuisinier</strong> ou <strong className="text-[#2A1D1A]">clé en main avec personnel</strong> — précisez votre choix dans le devis.
          </p>
        </FadeIn>
      </section>

      {/* AVIS CLIENTS */}
      <section className="bg-[#F2EBE1] py-24 lg:py-32">
        <div className="mx-auto max-w-7xl px-6 lg:px-10">
          <FadeIn className="flex flex-col items-start justify-between gap-6 md:flex-row md:items-end">
            <div>
              <Overline>Ils nous ont fait confiance</Overline>
              <h2 className="mt-4 max-w-xl font-serif-display text-4xl font-light text-[#2A1D1A] md:text-5xl">
                500+ événements par an, <span className="italic text-[#C05A45]">et ils reviennent</span>.
              </h2>
            </div>
            <a
              href={CONTACT.reviewsUrl}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="home-google-reviews"
              className="group font-serif-display text-2xl text-[#2A1D1A] transition-colors hover:text-[#C05A45]"
            >
              <span className="text-[#CBA135]">★★★★★</span> 4,9/5{" "}
              <span className="text-sm text-[#7A6B63] underline-offset-4 group-hover:underline">— avis Google de nos clients</span>
            </a>
          </FadeIn>
          <div className="mt-14 grid grid-cols-1 gap-6 md:grid-cols-3">
            {[
              { name: "Sophie & Marc", event: "Mariage, domaine des Monts d'Or", text: "Du vin d'honneur au brasero de fin de soirée, tout était parfait. Le dressage minute devant nos invités a bluffé tout le monde." },
              { name: "Karim B.", event: "Séminaire d'entreprise, Lyon Part-Dieu", text: "120 plateaux-repas livrés à l'heure pile, trois jours de suite. Équipe pro, carte variée, zéro stress pour nous." },
              { name: "Céline R.", event: "Anniversaire 50 ans, Valence", text: "La paella géante cuisinée au feu dans notre cour a fait l'unanimité. Devis clair, service impeccable, invités conquis." },
            ].map((r, i) => (
              <FadeIn key={r.name} delay={i * 0.08}>
                <figure data-testid={`review-${i}`} className="flex h-full flex-col rounded-3xl border border-[#E8E2D9] bg-[#FAF8F5] p-8">
                  <span className="text-lg text-[#CBA135]" aria-label="5 étoiles">★★★★★</span>
                  <blockquote className="mt-4 flex-1 text-sm leading-relaxed text-[#5c4d45] md:text-base">« {r.text} »</blockquote>
                  <figcaption className="mt-6 border-t border-[#E8E2D9] pt-4">
                    <p className="text-sm font-bold text-[#2A1D1A]">{r.name}</p>
                    <p className="text-xs text-[#7A6B63]">{r.event}</p>
                  </figcaption>
                </figure>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>

      {/* MANIFESTO */}
      <section className="mx-auto max-w-7xl px-6 py-28 lg:px-10 lg:py-36">
        <FadeIn>
          <Overline>Notre manifeste</Overline>
          <h2 className="mt-4 max-w-2xl font-serif-display text-4xl font-light leading-tight text-[#2A1D1A] md:text-5xl">
            Quatre savoir-faire, une seule exigence : <span className="italic text-[#C05A45]">le goût</span>.
          </h2>
        </FadeIn>

        <div className="mt-20 grid grid-cols-1 gap-x-12 gap-y-16 md:grid-cols-2">
          {SERVICES.map((s, i) => (
            <FadeIn key={s.num} delay={i * 0.08} className={i % 2 === 1 ? "md:translate-y-16" : ""}>
              <Link to={s.path} data-testid={`service-card-${s.num}`} className="group block border-t border-[#E8E2D9] pt-8">
                <div className="flex items-baseline justify-between">
                  <span className="font-serif-display text-6xl font-light text-[#CBA135]/60 transition-colors duration-500 group-hover:text-[#C05A45]">
                    {s.num}
                  </span>
                  <span className="text-xs uppercase tracking-[0.2em] text-[#7A6B63] opacity-0 transition-opacity duration-500 group-hover:opacity-100">
                    En savoir plus →
                  </span>
                </div>
                <h3 className="mt-4 font-serif-display text-3xl font-medium text-[#2A1D1A]">{s.title}</h3>
                <p className="mt-4 max-w-md text-sm leading-relaxed text-[#7A6B63] md:text-base">{s.desc}</p>
                <div className="mt-6 flex flex-wrap gap-2">
                  {s.tags.map((t) => (
                    <span key={t} className="rounded-full border border-[#E8E2D9] px-4 py-1.5 text-xs font-medium text-[#7A6B63]">
                      {t}
                    </span>
                  ))}
                </div>
              </Link>
            </FadeIn>
          ))}
        </div>
      </section>

      {/* BRASERO SPOTLIGHT */}
      <section className="bg-[#F2EBE1] py-28 lg:py-36">
        <div className="mx-auto grid max-w-7xl grid-cols-1 items-center gap-14 px-6 lg:grid-cols-12 lg:gap-10 lg:px-10">
          <FadeIn className="lg:col-span-5">
            <Overline>Animation signature</Overline>
            <h2 className="mt-4 font-serif-display text-4xl font-light leading-tight text-[#2A1D1A] md:text-5xl">
              Le brasero <span className="italic text-[#C05A45]">&</span> la paella, en direct.
            </h2>
            <p className="mt-6 max-w-md text-sm leading-relaxed text-[#7A6B63] md:text-base">
              Un chef, un feu, un spectacle. Paella géante safranée, broches de viande au brasero,
              légumes grillés à la braise : vos invités assistent à la naissance de leur repas.
              De 30 à 500 convives, en intérieur comme en extérieur.
            </p>
            <div className="mt-10">
              <ArrowButton to="/devis" testId="brasero-cta-devis">Réserver l'animation</ArrowButton>
            </div>
          </FadeIn>
          <FadeIn delay={0.15} className="lg:col-span-6 lg:col-start-7">
            <div className="overflow-hidden rounded-t-full">
              <motion.img
                initial={{ scale: 1.15 }}
                whileInView={{ scale: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 1.6, ease: [0.22, 1, 0.36, 1] }}
                src="/photos/kj6f5810.webp"
                alt="Dressage des assiettes en direct lors d'une animation brasero — réalisation Maison Désir & Gourmandise"
                className="aspect-[3/4] w-full object-cover"
              />
            </div>
          </FadeIn>
        </div>
      </section>

      {/* CHEF */}
      <section className="mx-auto max-w-7xl px-6 py-28 lg:px-10 lg:py-36">
        <div className="grid grid-cols-1 items-center gap-14 lg:grid-cols-12 lg:gap-10">
          <FadeIn className="lg:col-span-5">
            <div className="overflow-hidden rounded-t-full">
              <motion.img
                initial={{ scale: 1.15 }}
                whileInView={{ scale: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 1.6, ease: [0.22, 1, 0.36, 1] }}
                src="/photos/evowrc6u.webp"
                alt="Le Chef Mathias dressant des mignardises au chocolat dans l'atelier Maison Désir & Gourmandise"
                className="aspect-[3/4] w-full object-cover"
              />
            </div>
          </FadeIn>
          <FadeIn delay={0.15} className="lg:col-span-6 lg:col-start-7">
            <Overline>L'art du fait maison</Overline>
            <h2 className="mt-4 font-serif-display text-4xl font-light leading-tight text-[#2A1D1A] md:text-5xl">
              Derrière chaque bouchée, <span className="italic text-[#C05A45]">le Chef Mathias</span>.
            </h2>
            <p className="mt-6 max-w-md text-sm leading-relaxed text-[#7A6B63] md:text-base">
              Dans nos ateliers de Bron et de Valence, tout est fait maison : des pièces cocktail
              dressées à la main aux mignardises chocolatées de fin de réception. Une exigence
              artisanale que nous revendiquons depuis 2012, pour vos événements comme pour nos
              plateaux-repas du quotidien.
            </p>
            <div className="mt-10">
              <ArrowButton to="/prestations" testId="chef-cta-prestations">Découvrir notre savoir-faire</ArrowButton>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* CITIES */}
      <section className="mx-auto max-w-7xl px-6 py-28 lg:px-10 lg:py-36">
        <FadeIn>
          <Overline>Trois villes, une maison</Overline>
          <h2 className="mt-4 max-w-2xl font-serif-display text-4xl font-light leading-tight text-[#2A1D1A] md:text-5xl">
            Présents là où vous recevez.
          </h2>
        </FadeIn>
        <div className="mt-16 grid grid-cols-1 gap-6 md:grid-cols-3">
          {[
            { slug: "lyon", city: "Lyon", region: "Métropole de Lyon · Rhône", hook: "Capitale de la gastronomie, notre base de Bron sert toute la métropole." },
            { slug: "valence", city: "Valence", region: "Drôme · Valence Romans", hook: "Notre maison historique, entre Vercors et vallée du Rhône.", sub: [["/traiteur-mariage-valence", "Traiteur mariage Valence"], ["/brasero-valence", "Brasero Valence"]] },
            { slug: "grenoble", city: "Grenoble", region: "Isère · Alpes", hook: "Des séminaires de la presqu'île aux réceptions en altitude." },
          ].map((c, i) => (
            <FadeIn key={c.slug} delay={i * 0.1}>
              <Link
                to={`/traiteur-${c.slug}`}
                data-testid={`city-card-${c.slug}`}
                className="group block border border-[#E8E2D9] bg-[#FAF8F5] p-10 transition-colors duration-500 hover:bg-[#2A1D1A]"
              >
                <span className="font-serif-display text-5xl font-light text-[#CBA135]/50">{`0${i + 1}`}</span>
                <h3 className="mt-6 font-serif-display text-3xl font-medium text-[#2A1D1A] transition-colors duration-500 group-hover:text-[#FAF8F5]">
                  Traiteur {c.city}
                </h3>
                <p className="mt-2 text-xs uppercase tracking-[0.2em] text-[#CBA135]">{c.region}</p>
                <p className="mt-5 text-sm leading-relaxed text-[#7A6B63] transition-colors duration-500 group-hover:text-[#B8A99F]">{c.hook}</p>
                <span className="mt-8 inline-block text-sm font-semibold text-[#C05A45] transition-transform duration-300 group-hover:translate-x-1.5 group-hover:text-[#CBA135]">
                  Traiteur à {c.city} →
                </span>
              </Link>
              {c.sub && (
                <div className="mt-4 flex flex-wrap gap-x-5 gap-y-2" data-testid={`city-card-${c.slug}-sublinks`}>
                  {c.sub.map(([to, label]) => (
                    <Link
                      key={to}
                      to={to}
                      data-testid={`city-card-${c.slug}-sub-${to.replace(/\//g, "")}`}
                      className="text-xs font-semibold text-[#C05A45] underline-offset-4 transition-colors hover:underline"
                    >
                      {label} →
                    </Link>
                  ))}
                </div>
              )}
            </FadeIn>
          ))}
        </div>
      </section>

      {/* NUMBERS + CTA */}
      <section className="bg-[#2A1D1A] py-28 lg:py-32">
        <div className="mx-auto max-w-7xl px-6 lg:px-10">
          <div className="grid grid-cols-2 gap-12 md:grid-cols-4">
            {[
              ["2012", "année de création"],
              ["500+", "événements par an"],
              ["3", "villes d'ancrage"],
              ["24h", "délai de devis"],
            ].map(([num, label], i) => (
              <FadeIn key={label} delay={i * 0.08}>
                <p className="font-serif-display text-5xl font-light text-[#CBA135] md:text-6xl">{num}</p>
                <p className="mt-3 text-xs uppercase tracking-[0.2em] text-[#B8A99F]">{label}</p>
              </FadeIn>
            ))}
          </div>
          <FadeIn delay={0.2} className="mt-24 text-center">
            <h2 className="mx-auto max-w-3xl font-serif-display text-4xl font-light leading-tight text-[#FAF8F5] md:text-5xl">
              Un événement en vue ? <span className="italic text-[#CBA135]">Parlons menu.</span>
            </h2>
            <div className="mt-10 flex justify-center">
              <ArrowButton to="/devis" dark testId="footer-cta-devis">Obtenir mon devis sous 24h</ArrowButton>
            </div>
          </FadeIn>
        </div>
      </section>
    </div>
  );
}
